import {
  queryKeyAdults,
  queryKeyCheckIn,
  queryKeyCheckOut,
  queryKeyChildren,
  queryKeyDestinationId,
  queryKeyDestinationName,
  queryKeySelectedOfferIds,
} from 'constants/url'
import { queryNullChildren } from 'constants/search'
import useQueryParams from 'hooks/useQueryParams'
import { encodeChildrenOccupancy, encodeOfferIds } from 'lib/search/searchUtils'
import { useMemo } from 'react'

type OfferLike = App.AnyOffer | App.OfferListMetaData

function normaliseOffers(offerOrOffers: OfferLike | Array<OfferLike>): Array<OfferLike> {
  return Array.isArray(offerOrOffers) ? offerOrOffers : [offerOrOffers]
}

function useMapSearchOfferUrl(
  navigateToStoreMap: boolean,
  offerOrOffers: OfferLike | Array<OfferLike>,
  filters?: App.OfferListFilters,
  mapSearchPath: string = '/search/map',
): string {
  const urlSearchParams = useQueryParams()
  const offers = useMemo(() => normaliseOffers(offerOrOffers), [offerOrOffers])

  const url = useMemo(() => {
    const nextParams = new URLSearchParams(urlSearchParams)
    nextParams.set(queryKeySelectedOfferIds, encodeOfferIds(offers))
    applyFallbackSearchParams(nextParams, filters)

    if (navigateToStoreMap) {
      return `/search/store/map?${nextParams.toString()}`
    }
    return `${mapSearchPath}?${nextParams.toString()}`
  }, [offers, urlSearchParams, navigateToStoreMap, filters, mapSearchPath])

  return url
}

function applyFallbackSearchParams(params: URLSearchParams, filters?: App.OfferListFilters) {
  ensureDestination(params, filters)
  ensureOccupancy(params, filters)
  ensureDates(params, filters)
}

function ensureDestination(params: URLSearchParams, filters?: App.OfferListFilters) {
  if (params.has(queryKeyDestinationId) && params.has(queryKeyDestinationName)) {
    return
  }

  if (filters?.destinationId && filters.destinationName) {
    if (!params.has(queryKeyDestinationId)) {
      params.set(queryKeyDestinationId, filters.destinationId)
    }
    if (!params.has(queryKeyDestinationName)) {
      params.set(queryKeyDestinationName, filters.destinationName)
    }
  }
}

function ensureOccupancy(params: URLSearchParams, filters?: App.OfferListFilters) {
  const hasAdults = params.has(queryKeyAdults)
  const hasChildren = params.has(queryKeyChildren)

  if (hasAdults && hasChildren) {
    return
  }

  const rooms = filters?.rooms
  if (rooms && rooms.length > 0) {
    if (!hasAdults) {
      params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    }
    if (!hasChildren) {
      params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))
    }
    return
  }

  if (!hasAdults) {
    params.set(queryKeyAdults, '2')
  }
  if (!hasChildren) {
    params.set(queryKeyChildren, queryNullChildren)
  }
}

function ensureDates(params: URLSearchParams, filters?: App.OfferListFilters) {
  if (!params.has(queryKeyCheckIn) && filters?.checkIn) {
    params.set(queryKeyCheckIn, filters.checkIn)
  }

  if (!params.has(queryKeyCheckOut) && filters?.checkOut) {
    params.set(queryKeyCheckOut, filters.checkOut)
  }
}

export default useMapSearchOfferUrl
